import { supportChatLoaded } from '@app/utils/supportChat';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { LATEST_CHANGELOG_VERSION } from '@components/ui/AccountMenu/ChangelogSheet';
import { AdvanceCallback } from '@components/block/Quest/Quest';
import { AnyStep } from '@components/block/Quest/utils';
import { Task } from '@dieterApi/task/useTaskQuery';
import {
  UserApplication,
  UserQuestionnaireApplication,
  UserQuestionnaireApplicationTopic,
  UserTopicApplications,
} from '@dieterApi/user/useUserQuery';
import { useUserUpdateNavStateMutation } from '@dieterApi/user/useUserUpdateNavStateMutation';
import { useMediaQuery } from '@mui/material';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { Updater, useImmer } from 'use-immer';
import { MessageNames } from './MessageContext';
import {
  DieterHost,
  NavigationSection,
  NavigationSubsection,
  OnboardingApps,
  Topics,
  initialNavigationState,
} from './constants';
import { parseNavState } from './utils';

interface INavigationContext {
  navigation: NavigationState;
  setNavigation: Updater<NavigationState>;
  purgeNavigation: () => void;
  hasLoaded: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const NavigationContext = createContext<INavigationContext>({} as INavigationContext);

interface Props {
  children: React.ReactNode;
}

const NAV_STATE_EXPIRY = 1000 * 60 * 60 * 24 * 7; // 1 week

// we separate between states which should not be considered after NAV_STATE_EXPIRY and persistent state
const VOLATILE_STATE: Array<keyof Partial<NavigationState>> = ['animateBadge', 'phoneCTAdismissed']; //'activeSection', 'activeSubSection',
const PERSISTENT_STATE: Array<keyof Partial<NavigationState>> = [
  'limitedOfferDismissed',
  'mfaNotificationDismissed',
  'klausBannerDismissed',
  'klausPanelDismissed',
  'onboarding',
  'preferredTab',
  'languageDisclaimerDismissed',
  'expertModeEnabled',
  'superExpertModeEnabled',
  'justFinishedQuestionnaireRemoteId',
  'taskTutorials',
  'newTasks',
  'dismissedTasks',
  'dismissedMessages',
  'newDocuments',
  'showTopicCompleteConfirmation',
  'revisitTutorialSeen',
  'chatsViewed',
  'chatLastReadMessageIds',
  'isV2OnboardingMode',
  'stateMachine',
  'adminPanel',
  'changelogLastSeenVersion',
];

export function NavigationProvider({ children }: Props) {
  const { user, topics } = useUserWithCompany();

  const [navigation, setNavigation] = useImmer<NavigationState>(initialNavigationState);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [updateNavState] = useUserUpdateNavStateMutation();
  const isMobile = useMediaQuery('(max-width:767px)');

  // always save previous navigation state in ref
  const prevNavigation = usePrevious(navigation);

  // Track if we're currently loading to prevent saves during initial load
  const isLoadingRef = useRef(false);

  // Track pending save timeout for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track last saved state to prevent duplicate saves
  const lastSavedStateRef = useRef<string | null>(null);

  // Track if a save is in progress to prevent concurrent saves
  const isSavingRef = useRef(false);

  // PURGE NAVIGATION STATE
  const purgeNavigation = () => {
    setNavigation(initialNavigationState);
  };

  useEffect(() => {
    setNavigation(
      (nav) =>
        void (nav.isDieterHost = [DieterHost.Local, DieterHost.Stage, DieterHost.Prod].includes(
          window.location.hostname as DieterHost
        ))
    );
  }, [setNavigation]);

  useEffect(() => {
    if (!user) setHasLoaded(false);
  }, [user]);

  // LOAD NAVIGATION STATE FROM USER
  useEffect(() => {
    if (user) {
      isLoadingRef.current = true;

      if (user.navState) {
        // only restore navState if it was persisted not longer than an hour ago
        const newState = parseNavState(user.navState);

        let newVolatileState: Partial<NavigationState> = {};
        if (newState.lastUpdate && Date.now() - newState.lastUpdate.getTime() < NAV_STATE_EXPIRY) {
          // only set this if NAV_STATE_EXPIRY has not passed
          newVolatileState = filterObjectByKeys(newState, VOLATILE_STATE) as Partial<NavigationState>;
        }
        const newPersistentState = filterObjectByKeys(newState, PERSISTENT_STATE) as Partial<NavigationState>;

        setNavigation((draft) => {
          Object.assign(draft, newVolatileState, newPersistentState);
        });
      } else {
        // New user with no saved state — suppress changelog notification
        setNavigation((draft) => {
          draft.changelogLastSeenVersion = LATEST_CHANGELOG_VERSION;
        });
      }

      // Use setTimeout to ensure state updates complete before allowing saves
      setTimeout(() => {
        setHasLoaded(true);
        isLoadingRef.current = false;
      }, 500);
    }
  }, [user?.id, setNavigation]);

  useEffect(() => {
    user &&
      setNavigation(
        (nav) => void (nav.dashboardLocked = (user.hasPassword === false && !user.mittwaldUserId) || !user.confirmed)
      );
  }, [user?.hasPassword, user?.conditionsConsent, user?.mittwaldUserId, user?.confirmed, user, setNavigation]);

  // SAVE NAVIGATION STATE TO DATABASE (debounced, only if it has been loaded before)
  useEffect(() => {
    // Don't save if we're still loading or if hasLoaded is false
    if (!user || !hasLoaded || isLoadingRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[NavigationContext] Skip save:', { hasUser: !!user, hasLoaded, isLoading: isLoadingRef.current });
      }
      return;
    }

    // Compare navigation and prevNavigation to check if persistent state changed
    const changedKeys: string[] = [];
    const persistentStateHasChanged = Object.entries(navigation).map(([key, value]) => {
      if (
        VOLATILE_STATE.includes(key as keyof NavigationState) ||
        PERSISTENT_STATE.includes(key as keyof NavigationState)
      ) {
        const prevValue = prevNavigation && prevNavigation[key as keyof NavigationState];
        const changed = JSON.stringify(value) !== JSON.stringify(prevValue);
        if (changed) {
          changedKeys.push(key);
          if (process.env.NODE_ENV === 'development') {
            console.log(`[NavigationContext] "${key}" changed:`, { prev: prevValue, current: value });
          }
        }
        return changed;
      }
    });

    if (!persistentStateHasChanged.some(Boolean)) {
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[NavigationContext] Persistent state changed, scheduling save...', {
        changedKeys,
      });
    }

    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Debounce the save operation (500ms)
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null; // Clear ref since we're executing now

      // Double-check conditions before saving
      if (!user || isLoadingRef.current || isSavingRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NavigationContext] Skip actual save:', {
            hasUser: !!user,
            isLoading: isLoadingRef.current,
            isSaving: isSavingRef.current,
          });
        }
        return;
      }

      const stateItems: any = {};
      for (const key of VOLATILE_STATE.concat(PERSISTENT_STATE)) {
        stateItems[key] = navigation[key];
      }

      const stateToPersist = {
        ...stateItems,
        lastUpdate: new Date(),
      };

      const newNavState = JSON.stringify(stateToPersist);

      // Prevent duplicate saves by comparing with last saved state
      if (lastSavedStateRef.current === newNavState) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[NavigationContext] Skip save - state unchanged from last save');
        }
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[NavigationContext] Saving navigation state to database...', {
          stateMachine: navigation.stateMachine,
        });
      }

      // Mark as saving to prevent concurrent saves
      isSavingRef.current = true;
      lastSavedStateRef.current = newNavState;

      updateNavState({
        variables: {
          navState: newNavState,
        },
        optimisticResponse: {
          updateUserNavState: {
            id: user.id,
            navState: newNavState,
          } as any, // Cast to any because Apollo needs __typename but our type doesn't include it
        },
      })
        .then(() => {
          if (process.env.NODE_ENV === 'development') {
            console.log('[NavigationContext] Save complete');
          }
        })
        .catch((error) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('[NavigationContext] Save failed:', error);
          }
        })
        .finally(() => {
          isSavingRef.current = false;
        });
    }, 500);
  }, [navigation, user, hasLoaded, prevNavigation, updateNavState]);

  // set isMobile
  useEffect(() => {
    setNavigation((nav) => void (nav.isMobile = isMobile));
  }, [isMobile, setNavigation]);

  // set onboardingApplication
  useEffect(() => {
    setNavigation(
      (nav) =>
        void (nav.onboardingApp = topics.flatMap((t) => t.applications).find((app) => app.id === nav.onboarding.appId))
    );
  }, [navigation.onboarding.appId, topics, setNavigation]);

  const checkHubspotChatStatus = useCallback(() => {
    // check if the chat is loaded and/or open
    const isLoaded = supportChatLoaded();
    setNavigation((nav) => {
      // Only update if the value actually changed to avoid unnecessary re-renders
      if (nav.hubspot.chatIsLoaded !== isLoaded) {
        nav.hubspot.chatIsLoaded = isLoaded;
      }
    });
  }, [setNavigation]);

  // initialise Roadblock behaviour
  useEffect(() => {
    setNavigation(
      (nav) =>
        void (nav.roadBlock.open = (topic: UserTopicApplications) =>
          setNavigation((nav) => {
            nav.roadBlock.modalOpen = true;
            nav.roadBlock.topic = topic;
          }))
    );
    setNavigation(
      (nav) =>
        void (nav.roadBlock.close = () =>
          setNavigation((nav) => {
            nav.documentPaper = {
              modalOpen: false,
              questionnaire: undefined,
              index: 0,
            };
            nav.roadBlock.modalOpen = false;
            nav.roadBlock.topic = undefined;
          }))
    );
    setNavigation(
      (nav) =>
        void (nav.roadBlockDSFA.open = () =>
          setNavigation((nav) => {
            nav.roadBlockDSFA.modalOpen = true;
          }))
    );
    setNavigation(
      (nav) =>
        void (nav.roadBlockDSFA.close = () =>
          setNavigation((nav) => {
            nav.documentPaper = {
              modalOpen: false,
              questionnaire: undefined,
              index: 0,
            };
            nav.roadBlockDSFA.modalOpen = false;
          }))
    );

    // Check hubspot status less frequently (every 3 seconds instead of every second)
    const interval = setInterval(checkHubspotChatStatus, 3000);

    return () => clearInterval(interval);
  }, [setNavigation, checkHubspotChatStatus]);

  return (
    <NavigationContext.Provider value={{ navigation, setNavigation, purgeNavigation, hasLoaded }}>
      {children}
    </NavigationContext.Provider>
  );
}

export type NavigationScope = 'dashboard' | 'dashboardv2' | 'quest' | 'quest_external' | 'onboarding' | 'audit';

export type OnboardingAppMapping<T> = { [key in OnboardingApps]?: T };

export interface PendingNotificationOpen {
  action: 'open-chat';
  applicationId: string;
  section?: number;
}

interface MessageHash {
  name: MessageNames;
  contextHash: string;
}

export interface NavigationState {
  isDieterHost: boolean;
  scope?: NavigationScope;
  activeSection?: NavigationSection;
  activeTopic?: Topics;
  activeSubSection?: NavigationSubsection;
  onboarding: {
    appId?: OnboardingApps;
    inProgress: boolean;
  };

  onboardingApp?: UserApplication;
  preferredTab?: 'Documents' | 'Processes' | 'Forms' | null; // this should determine if we move to documents or tasks when opening a topic
  pendingNotificationOpen: PendingNotificationOpen | null;
  questAutoExpandChat: boolean;
  dashboardLocked: boolean;
  passwordModalOpen: boolean;
  dismissedMessages: MessageHash[];
  animateBadge: boolean;
  questionnaireWrapup: boolean; // this indicates that the finishing tasks are still in progress
  revisitTutorialSeen: boolean;
  isPrintView: boolean;
  itemListOpen: boolean;
  expertModeEnabled: boolean;
  superExpertModeEnabled: boolean;
  documentPaper: {
    modalOpen: boolean;
    index: number;
    questionnaire?: UserQuestionnaireApplicationTopic;
  };
  // This is a modal which is shown when the questionnaire was marked as UNTOUCHED or INCOMPLETE after a version update
  // and the user tried to open a document. It informs the user about the update and closes the document.
  questionnaireHasChanged: {
    modalOpen: boolean;
    questionnaire?: UserQuestionnaireApplication;
  };
  phoneCTAdismissed: boolean;
  limitedOfferDismissed: boolean;
  mfaNotificationDismissed: boolean;
  // Klaus cross-promo: both are dismissed for good once the user closes them
  klausBannerDismissed: boolean;
  klausPanelDismissed: boolean;
  justFinishedQuestionnaireRemoteId?: string | null;
  roadBlock: {
    modalOpen: boolean;
    topic?: UserTopicApplications;
    open: (topic: UserTopicApplications) => void;
    close: () => void;
  };
  roadBlockDSFA: {
    modalOpen: boolean;
    open: () => void;
    close: () => void;
  };

  languageDisclaimerDismissed: { [key in Topics]?: boolean };

  isV2OnboardingMode: boolean; // indicates if the user is in the V2 mode (new design)
  isMobile: boolean;
  lastUpdate?: Date;
  showTopicCompleteConfirmation: any;
  taskTutorials: any;
  // tutorialActive: string;
  taskOpen: {
    tutorial: string;
    confirm: string;
  };
  animate: boolean;
  newTasks: any;
  dismissedTasks: Record<string, boolean>;
  newDocuments: any;
  progress: number;
  boom: boolean; // triggers a bigBlast fireworks animation
  showSavedAnimation: boolean; // for showing a small notification in the navigation, whenever an answer was saved
  hubspot: {
    showChat: boolean;
    chatIsLoaded: boolean;
    chatIsOpen: boolean;
  };
  adminPanel: {
    companyId?: string;
    companyLabel?: string;
    selectedQuestionnaireId?: string;
  };
  chatsViewed: string[];
  chatLastReadMessageIds: Record<string, string>; // Maps chatId to lastReadMessageId for unread indicator
  changelogLastSeenVersion?: string; // Last changelog version the user has seen
  changelogOpen?: boolean; // Whether the changelog sheet is open
  stateMachine?: {
    state: string;
    context: {
      appId?: string;
      questionnaireId?: string;
      email?: string;
      confirmationToken?: string;
    };
  };
  consent: {
    hubspot: boolean;
    sentry: boolean;
  };
}

// from scope: quest
export interface NavigationState {
  stepIndex: number;
  steps: Array<AnyStep>;
  onAdvance: AdvanceCallback;
  questTask: Task;
}

const filterObjectByKeys = (raw: any, keys: Array<string>) => {
  return Object.keys(raw)
    .filter((key) => keys.includes(key))
    .reduce((obj: any, key: any) => {
      obj[key] = raw[key];
      return obj;
    }, {});
};

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
